import React, { useCallback, useRef, useState } from 'react';

import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';

import OrderSearchForm, { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';
import PromoCodeNameField from '~/components/Promo/PromoCodeNameField';

import { getOrdersByIds, getPurchases, multiSearch, searchPurchases } from '~/services/OrdersService';
import { debugPromoByOrderID } from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';

import PromoSimulationMultiResult from './PromoSimulationMultiResult';
import {
  DiscountCalculationResult,
  OrderRecord,
  SimulationRow,
  getMultiSimulationInputs,
} from './multiSimulationResults';

type Source = 'order-ids' | 'last' | 'page' | 'random';
type SearchDefinition = {
  filterField: string;
  filterValue: string;
  additionalFilter?: MultiFilterState;
  businessId?: string | number;
};

const MAX_ORDERS = 25;
const RANDOM_PAGE_SEARCHES = 3;
const REQUEST_GAP_MS = 350;
const LAST_ORDER_ITEM_TYPES = [
  { label: 'All item types', value: '' },
  { label: 'Accommodation', value: 'accommodation' },
  { label: 'Flights', value: 'flight' },
  { label: 'Tours', value: 'tour' },
  { label: 'Cruises', value: 'cruise' },
  { label: 'Car hire', value: 'car_hire' },
  { label: 'Insurance', value: 'insurance' },
];

function stringValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function isOrderRecord(value: unknown): value is OrderRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function orderRecords(value: unknown): Array<OrderRecord> {
  return Array.isArray(value) ? value.filter(isOrderRecord) : [];
}

function orderIdFor(order: OrderRecord): string {
  return stringValue(order.id_orders ?? order.id);
}

function orderNumberFor(order: OrderRecord): string {
  return stringValue(order.order_number ?? order.orderNumber ?? order.booking_number);
}

function formatOrderTotal(order: OrderRecord): string {
  const total = Number(order.total ?? order.total_amount ?? order.totalAmount ?? order.grand_total);
  const currency = stringValue(order.currency_code ?? order.currencyCode ?? order.currency) || 'AUD';
  return Number.isFinite(total) ? currencyFormatter(currency, total, 2) : '-';
}

function makeRow(order: OrderRecord): SimulationRow {
  const orderId = orderIdFor(order);
  return {
    id: orderId,
    orderId,
    orderNumber: orderNumberFor(order),
    createdAt: stringValue(order.created_at ?? order.createdAt),
    total: formatOrderTotal(order),
    status: stringValue(order.status ?? order.order_status),
    calculatorStatus: 'pending',
    promoCode: '',
    discount: '',
    vouchers: '',
    error: '',
    sourceOrder: order,
    submittedVoucherIds: [],
    debugData: order,
  };
}

function tokenizeIds(value: string): Array<string> {
  return Array.from(
    new Set(
      value
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  );
}

function parseOrderIds(value: string): Array<string> {
  return tokenizeIds(value).slice(0, MAX_ORDERS);
}

function randomItems<T>(items: Array<T>, count: number): Array<T> {
  return [...items].sort(() => Math.random() - 0.5).slice(0, count);
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

export default function PromoSimulatorMulti({ tenant }: { tenant: App.Tenant }) {
  const [source, setSource] = useState<Source>('page');
  const [orderIdsText, setOrderIdsText] = useState('');
  const [page, setPage] = useState(1);
  const [sampleSize, setSampleSize] = useState(10);
  const [lastOrderItemType, setLastOrderItemType] = useState('');
  const [searchDefinition, setSearchDefinition] = useState<SearchDefinition>();
  const [codeName, setCodeName] = useState('');
  const [voucherIdsText, setVoucherIdsText] = useState('');
  const [useOriginalPromoVoucher, setUseOriginalPromoVoucher] = useState(true);
  const [applyWithOriginalUserAccount, setApplyWithOriginalUserAccount] = useState(false);
  const [ignoreLuxPlusPricing, setIgnoreLuxPlusPricing] = useState(true);
  const [rows, setRows] = useState<Array<SimulationRow>>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const runIdRef = useRef(0);
  const orderSearchFormRef = useRef<HTMLDivElement>(null);
  const searchDefinitionRef = useRef<SearchDefinition | undefined>(undefined);
  const additionalFilterRef = useRef<MultiFilterState | undefined>(undefined);

  const updateRow = useCallback((orderId: string, update: Partial<SimulationRow>) => {
    setRows((currentRows) => currentRows.map((row) => (row.orderId === orderId ? { ...row, ...update } : row)));
  }, []);

  const fetchPage = useCallback(
    async (pageNumber: number, perPage: number): Promise<{ orders: Array<OrderRecord>; total: number }> => {
      const definition = searchDefinitionRef.current;
      if (!definition) throw new Error('Order search did not provide filters.');
      const { filterField, filterValue, additionalFilter, businessId } = definition;
      if (filterField === 'universal_search_id') {
        const result = await searchPurchases({
          page: pageNumber - 1,
          perPage,
          searchId: filterValue,
          orderBy: source === 'last' ? 'created_at' : undefined,
          orderDirection: source === 'last' ? 'desc' : undefined,
        });
        return { orders: orderRecords(result.result), total: Number(result.total ?? 0) };
      }
      if (filterField === 'multi_search') {
        const result = await multiSearch({
          page: pageNumber - 1,
          perPage,
          orderBy: 'created_at',
          orderDirection: 'desc',
          additionalFilter:
            source === 'last' && lastOrderItemType
              ? { ...additionalFilter, itemType: lastOrderItemType }
              : additionalFilter,
        });
        return { orders: orderRecords(result.result), total: Number(result.total ?? 0) };
      }

      const result = await getPurchases({
        page: pageNumber,
        per_page: perPage,
        brand: tenant.brand,
        filterBy: filterField,
        filterValue,
        additionalFilter:
          source === 'last' ? { ...additionalFilter, sortField: 'created_at', sortType: 'desc' } : additionalFilter,
        item_type: source === 'last' ? lastOrderItemType || undefined : undefined,
        businessId,
      });
      return { orders: orderRecords(result.result), total: Number(result.total ?? 0) };
    },
    [lastOrderItemType, source, tenant.brand],
  );

  const simulateRows = useCallback(
    async (
      selectedRows: Array<SimulationRow>,
      runId: number,
      runWithOriginalPromoVoucher = useOriginalPromoVoucher,
    ) => {
      const normalizedCodeName = codeName.trim();
      const voucherOverrides = tokenizeIds(voucherIdsText);
      for (const row of selectedRows) {
        if (runId !== runIdRef.current) {
          return;
        }
        updateRow(row.orderId, {
          calculatorStatus: 'running',
          error: '',
          result: undefined,
          discount: '',
          vouchers: '',
          debugData: undefined,
        });
        try {
          const inputs = getMultiSimulationInputs(
            row.sourceOrder,
            normalizedCodeName,
            voucherOverrides,
            runWithOriginalPromoVoucher,
          );
          const effectiveCodeName = inputs.codeName;
          updateRow(row.orderId, { submittedCodeName: effectiveCodeName, submittedVoucherIds: inputs.voucherIds });
          const response = await debugPromoByOrderID(
            effectiveCodeName,
            row.orderId,
            tenant.brand,
            applyWithOriginalUserAccount,
            ignoreLuxPlusPricing,
            () => undefined,
            inputs.voucherIds,
          );
          if (runId !== runIdRef.current) return;
          if (response.status !== 200) {
            updateRow(row.orderId, { calculatorStatus: 'error', error: JSON.stringify(response), debugData: response });
          } else {
            const result: DiscountCalculationResult = response.result;
            const { promo } = result;
            const currency = promo.currency;
            const discount = promo.discount_total;
            const voucherTotal = promo.voucherTotal;
            updateRow(row.orderId, {
              calculatorStatus: 'complete',
              result,
              promoCode: promo.code_name || effectiveCodeName,
              discount: Number.isFinite(discount) ? currencyFormatter(currency, discount, 2) : '-',
              vouchers: Number.isFinite(voucherTotal) ? currencyFormatter(currency, voucherTotal, 2) : '-',
              debugData: response,
            });
          }
        } catch (error) {
          if (runId !== runIdRef.current) return;
          updateRow(row.orderId, {
            calculatorStatus: 'error',
            error: error instanceof Error ? error.message : JSON.stringify(error),
            debugData: error,
          });
        }
        await delay(REQUEST_GAP_MS);
      }
    },
    [
      applyWithOriginalUserAccount,
      codeName,
      ignoreLuxPlusPricing,
      tenant.brand,
      updateRow,
      useOriginalPromoVoucher,
      voucherIdsText,
    ],
  );

  const runWithOriginalPromoVoucher = useCallback(() => {
    if (rows.length === 0) {
      return;
    }
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    void simulateRows(rows, runId, true);
  }, [rows, simulateRows]);

  const selectAndSimulate = useCallback(async () => {
    setIsSelecting(true);
    setSelectionError(null);
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    try {
      if (source !== 'order-ids') {
        searchDefinitionRef.current = undefined;
        orderSearchFormRef.current?.querySelector('form')?.requestSubmit();
        // The form validates and publishes its current filters synchronously.
        if (!searchDefinitionRef.current) return;
      }
      let selectedOrders: Array<OrderRecord>;
      const count = Math.max(1, Math.min(MAX_ORDERS, sampleSize));
      if (source === 'order-ids') {
        const ids = parseOrderIds(orderIdsText);
        if (ids.length === 0) {
          throw new Error('Enter at least one order ID.');
        }
        selectedOrders = orderRecords(await getOrdersByIds(ids, tenant.brand));
      } else if (source === 'last') {
        selectedOrders = (await fetchPage(1, count)).orders;
      } else if (source === 'page') {
        selectedOrders = (await fetchPage(Math.max(1, page), count)).orders;
      } else {
        const firstPage = await fetchPage(1, 1);
        const totalPages = Math.max(1, Math.ceil(firstPage.total / count));
        const pageNumbers = Array.from(
          new Set(Array.from({ length: RANDOM_PAGE_SEARCHES }, () => 1 + Math.floor(Math.random() * totalPages))),
        );
        const pageResults = await Promise.all(pageNumbers.map((pageNumber) => fetchPage(pageNumber, count)));
        selectedOrders = randomItems(
          pageResults.flatMap((result) => result.orders),
          count,
        );
      }

      const selectedRows = selectedOrders.map(makeRow).filter((row) => Boolean(row.orderId));
      if (selectedRows.length === 0) {
        throw new Error('No orders matched this selection.');
      }
      setRows(selectedRows);
      void simulateRows(selectedRows, runId);
    } catch (error) {
      setSelectionError(error instanceof Error ? error.message : JSON.stringify(error));
    } finally {
      setIsSelecting(false);
    }
  }, [fetchPage, orderIdsText, page, sampleSize, simulateRows, source, tenant.brand]);

  const handleOrderSearch = useCallback(
    (filterField: unknown, filterValue: unknown, params?: { businessId?: string | number }) => {
      const definition: SearchDefinition = {
        filterField: stringValue(filterField),
        filterValue: stringValue(filterValue),
        additionalFilter: additionalFilterRef.current,
        businessId: params?.businessId,
      };
      searchDefinitionRef.current = definition;
      setSearchDefinition(definition);
    },
    [],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Select a compact set of orders, then evaluate the discount calculator serially. Requests are spaced to avoid
        placing unnecessary load on promo services.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <PromoCodeNameField
          label="Promo code"
          value={codeName}
          onChange={(event) => setCodeName(event.target.value)}
          fullWidth
        />
        <TextField
          select
          label="Order source"
          value={source}
          onChange={(event) => setSource(event.target.value as Source)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="order-ids">Entered order IDs</MenuItem>
          <MenuItem value="last">Last matching orders</MenuItem>
          <MenuItem value="page">A search results page</MenuItem>
          <MenuItem value="random">Random search sample</MenuItem>
        </TextField>
        <TextField
          label="Orders to simulate"
          type="number"
          value={sampleSize}
          onChange={(event) => setSampleSize(Number(event.target.value))}
          inputProps={{ min: 1, max: MAX_ORDERS }}
          sx={{ minWidth: 150 }}
        />
        {source === 'last' && searchDefinition?.filterField !== 'universal_search_id' && (
          <TextField
            select
            label="Item type"
            value={lastOrderItemType}
            onChange={(event) => setLastOrderItemType(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            {LAST_ORDER_ITEM_TYPES.map((itemType) => (
              <MenuItem key={itemType.value || 'all'} value={itemType.value}>
                {itemType.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      <TextField
        label="Voucher IDs (optional)"
        value={voucherIdsText}
        onChange={(event) => setVoucherIdsText(event.target.value)}
        helperText="Optional override; otherwise vouchers already attached to each order are reapplied"
        fullWidth
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={useOriginalPromoVoucher}
            onChange={(event) => setUseOriginalPromoVoucher(event.target.checked)}
          />
        }
        label="Use original promo/voucher from each order"
      />
      <Stack direction="row" spacing={1}>
        <Button
          variant={applyWithOriginalUserAccount ? 'contained' : 'text'}
          onClick={() => setApplyWithOriginalUserAccount((value) => !value)}
        >
          Original user account
        </Button>
        <Button
          variant={ignoreLuxPlusPricing ? 'contained' : 'text'}
          onClick={() => setIgnoreLuxPlusPricing((value) => !value)}
        >
          Ignore LuxPlus pricing
        </Button>
      </Stack>

      {source === 'order-ids' ? (
        <TextField
          label="Order IDs"
          value={orderIdsText}
          onChange={(event) => setOrderIdsText(event.target.value)}
          multiline
          minRows={3}
          helperText={`Up to ${MAX_ORDERS} comma- or whitespace-separated IDs`}
          fullWidth
        />
      ) : (
        <>
          <Typography variant="subtitle2">Order filter</Typography>
          <Box ref={orderSearchFormRef}>
            <OrderSearchForm
              searchQuery={handleOrderSearch}
              setAdditionalParams={(additionalFilter: MultiFilterState) => {
                additionalFilterRef.current = additionalFilter;
                setSearchDefinition((current) =>
                  current
                    ? { ...current, additionalFilter }
                    : { filterField: 'created_at', filterValue: '', additionalFilter },
                );
              }}
              bookingNumberPlaceholder="Search orders or choose a filter"
              bookingIdType="universal_search_id"
              showMultiSearch
              showOrderNotesFilter
            />
          </Box>
          {source === 'page' && (
            <TextField
              label="Results page"
              type="number"
              value={page}
              onChange={(event) => setPage(Number(event.target.value))}
              inputProps={{ min: 1 }}
              sx={{ maxWidth: 180 }}
            />
          )}
        </>
      )}

      {selectionError && <Alert severity="error">{selectionError}</Alert>}
      <Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" onClick={selectAndSimulate} disabled={isSelecting}>
            {isSelecting ? 'Selecting orders…' : 'Select and simulate'}
          </Button>
          <Button variant="text" onClick={runWithOriginalPromoVoucher} disabled={isSelecting || rows.length === 0}>
            Run with original promo/voucher
          </Button>
        </Stack>
      </Box>
      {rows.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Results ({rows.length} orders)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {rows.filter((row) => row.calculatorStatus === 'complete').length} complete ·{' '}
            {rows.filter((row) => row.calculatorStatus === 'error').length} failed. Expand an order to compare item
            prices and review warnings.
          </Typography>
          {rows.map((row) => (
            <PromoSimulationMultiResult key={row.orderId} row={row} />
          ))}
        </Box>
      )}
    </Stack>
  );
}
